import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchCorporateUserOrgs, acceptCorporateInvite } from '../services/corporateService';

/**
 * Returns the signed-in user's corporate org memberships.
 *
 * Shape: { orgs: [{orgId, orgName, roles}], loading: boolean }
 *
 * Calls getCorporateUserOrgs once per auth session and caches the result
 * in module-level state keyed by uid so repeated mounts don't re-fetch.
 */

const cache = new Map();

export default function useCorporateOrgs() {
  const { currentUser, loading: authLoading } = useAuth();
  const uid = currentUser?.uid || null;

  const [orgs, setOrgs] = useState(() => (uid && cache.has(uid) ? cache.get(uid) : []));
  const [loading, setLoading] = useState(() => uid ? !cache.has(uid) : false);

  useEffect(() => {
    if (authLoading || !uid) {
      setOrgs([]);
      setLoading(false);
      return;
    }

    if (cache.has(uid)) {
      setOrgs(cache.get(uid));
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    // Auto-accept any pending invites before fetching org list
    acceptCorporateInvite()
      .catch((err) => {
        console.warn('[useCorporateOrgs] acceptCorporateInvite failed:', err?.message || err);
      })
      .then(() => fetchCorporateUserOrgs())
      .then((memberships) => {
        if (!cancelled) {
          cache.set(uid, memberships);
          setOrgs(memberships);
        }
      })
      .catch((err) => {
        console.warn('[useCorporateOrgs] Failed to load org memberships:', err?.message || err);
        if (!cancelled) setOrgs([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [uid, authLoading]);

  return { orgs, loading };
}
